import moment from 'moment';
import { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Spinner } from '../../../../../components/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { getDaysInMonth, nextYears, previousYears } from '../../../../../utils/dates';
import { useAuth } from '../../../../../hooks/useAuth';
import { useEnv } from '../../../../../context/env-context';
import { toast } from 'react-toastify';
import { makeRequest } from '../../../../../utils/makeRequest';

function NewFundingLatestRepaymentDateForm({
  fundingId,
  fundingMaxRepaymentDate,
  refreshFundingData,
}) {
  const { apiOriginFunding } = useEnv();
  const { getAccessTokenSilently } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [spinner, setSpinner] = useState(false);

  const [latestRepaymentDate, setLatestRepaymentDate] = useState({
    year: String(moment(fundingMaxRepaymentDate).year()),
    month: String(moment(fundingMaxRepaymentDate).month()),
    date: String(moment(fundingMaxRepaymentDate).date()),
  });

  const handleToggle = () => {
    setIsOpen(prev => !prev);
    setLatestRepaymentDate({
      year: String(moment(fundingMaxRepaymentDate).year()),
      month: String(moment(fundingMaxRepaymentDate).month()),
      date: String(moment(fundingMaxRepaymentDate).date()),
    });
  };

  const setRapaymentDate = async () => {
    setSpinner(true);
    const token = await getAccessTokenSilently();

    if (!token) {
      return;
    }

    const config = {
      token,
      url: `${apiOriginFunding}/set_latest_date`,
      method: 'POST',
      data: JSON.stringify({
        fundingId,
        latestDate: moment({
          year: String(moment(latestRepaymentDate).year()),
          month: String(moment(latestRepaymentDate).month()),
          date: String(moment(latestRepaymentDate).date()),
        }).format('YYYY-MM-DD'),
      }),
    };

    try {
      await makeRequest(config);
      await refreshFundingData();

      handleToggle();

      toast.success('Latest repayment date updated successfully');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSpinner(false);
    }
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <p className="bo-common-text">
        {fundingMaxRepaymentDate
          ? moment.utc(fundingMaxRepaymentDate).local().format('DD MMM, YYYY')
          : '---'}
      </p>
      <OverlayTrigger
        trigger="click"
        placement="bottom-end"
        show={isOpen}
        popperConfig={{ modifiers: [{ name: 'offset', options: { offset: [0, 0] } }] }}
        overlay={
          <Popover className="custom-popover">
            <Popover.Body className="bo-common-popover-form-menu">
              <Spinner visible={spinner} size={36}>
                <Form.Label className="bo-common-popover-form-menu-label">Year</Form.Label>
                <Form.Select
                  className="bo-common-popover-form-menu-input bo-w-180"
                  value={latestRepaymentDate.year}
                  onChange={e => {
                    if (
                      !moment({
                        year: e.target.value,
                        month: latestRepaymentDate.month,
                        date: latestRepaymentDate.date,
                      }).isValid()
                    ) {
                      setLatestRepaymentDate({
                        ...latestRepaymentDate,
                        year: e.target.value,
                        date: moment({ year: e.target.value, month: latestRepaymentDate.month })
                          .endOf('month')
                          .date(),
                      });
                    } else {
                      setLatestRepaymentDate({ ...latestRepaymentDate, year: e.target.value });
                    }
                  }}
                >
                  {[...previousYears(10), ...nextYears(20, moment().add(1, 'year').year())].map(
                    year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ),
                  )}
                </Form.Select>
                <Form.Label className="bo-common-popover-form-menu-label mt-2">Month</Form.Label>
                <Form.Select
                  className="bo-common-popover-form-menu-input bo-w-180"
                  value={latestRepaymentDate.month}
                  onChange={e => {
                    if (
                      !moment({
                        year: latestRepaymentDate.year,
                        month: e.target.value,
                        date: latestRepaymentDate.date,
                      }).isValid()
                    ) {
                      setLatestRepaymentDate({
                        ...latestRepaymentDate,
                        month: e.target.value,
                        date: moment({ year: latestRepaymentDate.year, month: e.target.value })
                          .endOf('month')
                          .date(),
                      });
                    } else {
                      setLatestRepaymentDate({ ...latestRepaymentDate, month: e.target.value });
                    }
                  }}
                >
                  {[...Array(12)].map((_, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <option value={i} key={i}>
                      {moment().month(i).format('MMMM')}
                    </option>
                  ))}
                </Form.Select>
                <Form.Label className="bo-common-popover-form-menu-label mt-2">Day</Form.Label>
                <Form.Select
                  className="bo-common-popover-form-menu-input bo-w-180"
                  value={latestRepaymentDate.date}
                  onChange={e =>
                    setLatestRepaymentDate({ ...latestRepaymentDate, date: e.target.value })
                  }
                >
                  {getDaysInMonth(latestRepaymentDate.month, latestRepaymentDate.year).map(x => (
                    <option value={x} key={x}>
                      {x}
                    </option>
                  ))}
                </Form.Select>
                <div className="mt-2 d-flex justify-content-between">
                  <Button className="bo-common-button px-2" onClick={setRapaymentDate}>
                    Set
                  </Button>
                  <Button className="bo-common-button px-2" onClick={handleToggle}>
                    Cancel
                  </Button>
                </div>
              </Spinner>
            </Popover.Body>
          </Popover>
        }
      >
        <Button className="bo-common-base-button" onClick={handleToggle}>
          <FontAwesomeIcon icon={faPencil} color="#5946A4" size="sm" />
        </Button>
      </OverlayTrigger>
    </div>
  );
}

export default NewFundingLatestRepaymentDateForm;
